import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { AppNotification } from './notifications.api';
import { noticeFullTime, noticeKind, noticeWhen, safeNoticeLink } from './notificationKinds';

/**
 * One notice: the kind's glyph, a small label and the time on the tenant
 * clock, the title, one line of body. Unread carries the cobalt dot (a live
 * mark, static: nothing here moves). With a link it is a link (middle-click
 * and copy work); without one it is a button that only marks it read.
 */
export function NoticeRow({
  n,
  now,
  onOpen,
  roomy = false,
}: {
  n: AppNotification;
  now: Date;
  /** Called on activation, before the link is followed. */
  onOpen: (n: AppNotification) => void;
  /** The full page: two lines of body instead of one. */
  roomy?: boolean;
}) {
  const kind = noticeKind(n.type);
  const unread = !n.read_at;
  const to = safeNoticeLink(n.link);
  const cls = `notice${unread ? ' is-unread' : ''}${roomy ? ' notice--roomy' : ''}`;
  const label = `${unread ? 'Unread. ' : ''}${n.title}${n.body ? `. ${n.body}` : ''}`;

  const inner: ReactNode = (
    <>
      <span className="notice__glyph" aria-hidden>
        {kind.glyph(16)}
      </span>
      <span className="notice__text">
        <span className="notice__meta">
          <span className="notice__kind">{kind.label}</span>
          <time className="notice__when" dateTime={n.created_at} title={noticeFullTime(n.created_at)}>
            {noticeWhen(n.created_at, now)}
          </time>
        </span>
        <span className="notice__title">{n.title}</span>
        {n.body && <span className="notice__body">{n.body}</span>}
      </span>
      <span className={`notice__dot${unread ? '' : ' is-read'}`} aria-hidden />
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls} onClick={() => onOpen(n)} aria-label={label}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" className={cls} onClick={() => onOpen(n)} aria-label={label}>
      {inner}
    </button>
  );
}
